<script lang="ts">
  import { slide } from 'svelte/transition'
  export let type: 'general' | 'sales' | 'buyers' = 'general'
  let activeIndex: number | null = 0

  const faqs = {
    general: [
      {
        question: 'What is AddMeetings?',
        answer:
          'AddMeetings is an AI-powered meeting qualification platform that filters cold meeting requests so only the most relevant, high-value ones reach your inbox and calendar. It eliminates manual vetting, endless back-and-forth, and irrelevant outreach, helping you focus on conversations that truly move your business forward.',
      },
      {
        question: 'How does AddMeetings work for B2B professionals?',
        answer:
          'You sign up for free, share your meeting intent, and connect your calendar. Then simply say, “Meet me on AddMeetings.” Our intent-based AI screens and scores every incoming meeting request so you only receive qualified, relevant opportunities directly in your inbox or calendar.',
      },
      {
        question: "What's in it for sales professionals using AddMeetings?",
        answer:
          'Sales professionals can use AddMeetings to connect with decision-makers who are actually open to relevant meetings. Instead of cold outreach and spam, AddMeetings helps you reach the right people through per-qualified, intent-based connections.',
      },
      {
        question: 'Who founded AddMeetings?',
        answer:
          'AddMeetings was founded by Kiran Ramaiah, a second-time startup founder originally from India and now based in Oslo. His mission is to make cold meetings more meaningful by removing the noise from business outreach.',
      },
      {
        question: 'What is the current status of AddMeetings?',
        answer:
          'AddMeetings is live with Google Calendar integration and continuously evolving with regular updates. Upcoming features include Outlook and Apple Calendar support, browser and email extensions, and AI-powered meeting filtering and analytics, with steady growth driven by our expanding community of B2B professionals.',
      },
      {
        question: 'How can one join AddMeetings?',
        answer:
          'Simply sign up for free, set your meeting intent, and connect your calendar. Once done, share your AddMeetings link in your email signature, messages, or on LinkedIn, or simply say “Meet me on AddMeetings” to start receiving only qualified meeting requests.',
      },
      {
        question: 'What is the mission of AddMeetings?',
        answer:
          'Our mission is to help B2B professionals spend less time filtering noise and more time having the right conversations. By qualifying meetings before they happen, AddMeetings ensures every interaction is relevant, valuable, and worth your time enabling meaningful conversations that move business forward.',
      },
    ],
    buyers: [
      {
        question: 'What is AddMeetings?',
        answer:
          'AddMeetings is an AI-powered meeting qualification platform that filters cold meeting requests so only the most relevant, high-value ones reach your inbox and calendar. It helps B2B professionals skip irrelevant outreach, save time, and focus only on conversations that move business forward.',
      },
      {
        question: 'What can I use AddMeetings for as a B2B professional?',
        answer:
          'Use AddMeetings to handle all your external meeting requests  from cold outreach to partnership offers. Instead of getting spammed with random pitches, your AddMeetings link automatically screens and scores requests based on your preferences, so you only engage with qualified, relevant opportunities.',
      },
      {
        question: 'Why should I put my availability here instead of just sharing my calendar?',
        answer:
          'A regular calendar link books anyone and still leaves you doing the manual work of sharing links and qualifying requests. AddMeetings handles it all. It screens every request using intent-based AI so your time is reserved only for the conversations that truly matter. And the best part? You don’t even have to send links, just say “Meet me on AddMeetings.',
      },
      {
        question: 'Is AddMeetings free to use?',
        answer:
          'Yes, AddMeetings is free to use. You can upgrade later to unlock premium features like meeting analytics, AI-powered feedback, and advanced scheduling integrations that are currently in development.',
      },
      {
        question: 'How does AddMeetings ensure the quality of meeting requests?',
        answer:
          'Every request goes through a multi-layer qualification process powered by intent-based AI. We analyze context, relevance, and fit to ensure only high-value meeting requests reach your inbox while the rest get filtered out automatically.',
      },
      {
        question: 'Can I get compensated for taking meetings?',
        answer:
          'Every meeting request goes through three layers of qualification to ensure it\’s relevant and aligned with your intent. You can also choose to set a fee for meetings if you’d like to ensure you\’re connecting with people who truly value your time. It\’s optional.',
      },
      {
        question: 'How do I get started with AddMeetings?',
        answer:
          'Simply sign up for free, set your meeting intent, and connect your calendar. Then share your AddMeetings link anywhere in your email signature, messages, or on LinkedIn or simply say “Meet me on AddMeetings” to start receiving only qualified meeting requests.',
      },
    ],
    sales: [
      {
        question: 'What is AddMeetings?',
        answer:
          'AddMeetings is an AI-powered platform that helps solution providers and sales professionals connect directly with relevant decision-makers. Instead of sending endless cold emails, AddMeetings qualifies your prospects and ensures your outreach reaches the right buyers who are open to meetings.',
      },
      {
        question: 'How does AddMeetings help solution providers?',
        answer:
          'AddMeetings replaces guesswork with precision. You pitch once, and the AI filters and matches your request to potential buyers based on their business intent. That means fewer wasted emails and more meetings with prospects who actually care about what you offer.',
      },
      {
        question: 'What kind of meetings can I request through AddMeetings?',
        answer:
          'You can request discovery, partnership, demo, or consultation meetings  anything that helps you start a valuable business conversation. AddMeetings ensures every request is screened and relevant before it reaches a buyer\’s inbox.',
      },
      {
        question: 'How does AddMeetings improve my outreach success rate?',
        answer:
          'By eliminating spam and targeting relevance. When buyers use AddMeetings, they set their meeting intent and preferences. Your request is automatically matched only if it fits those criteria giving you a much higher chance of getting accepted.',
      },
      {
        question: 'Can I customize or track my meeting performance?',
        answer:
          'Yes. AddMeetings provides smart analytics and insights (coming soon) so you can see which types of pitches convert best and refine your outreach strategy over time.',
      },
      {
        question: 'How is AddMeetings different from other scheduling tools?',
        answer:
          'Traditional tools just book time on a calendar AddMeetings qualifies meetings before they happen. It combines outreach, filtering, and intent-based AI scoring, so you only spend time on high-value, relevant conversations.',
      },
      {
        question: 'Is AddMeetings free to use?',
        answer:
          'Yes. You can start for free and get 200 joining credits to explore the platform and request meetings with existing users.Premium features like AI-powered analytics, meeting feedback, and CRM integrations will be available in upcoming releases.',
      },
      {
        question: 'How do I get started?',
        answer:
          'Sign up for free, set your pitch and meeting intent, and start sending qualified meeting requests directly to relevant buyers already on the platform. As a user, you can both pitch your solutions and accept meeting requests from others to reduce the noise and keep your calendar focused on what matters. Share your AddMeetings profile anywhere in your email signature, on LinkedIn, or your website, or simply say “Meet me on AddMeetings” and start having the right conversations.',
      },
    ],
  }

  function toggleAccordion(index: number) {
    activeIndex = activeIndex === index ? null : index
  }
</script>

<section class="py-24 flex flex-col justify-center">
  <div class="container mx-auto px-4 max-w-4xl">
    <div class="flex flex-col items-center text-center mb-16 gap-3">
      <h2 class="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h2>
      <p class="text-am-black-75 mb-2 max-w-xl">
        These are questions some of our best users ask about AddMeetings. Can't find something for you?
        <a href="mailto:support@addmeetings.com" class="underline hover:text-am-blue transition">Ask us directly.</a>
      </p>
    </div>

    <div class="space-y-8">
      {#each faqs[type] as faq, i}
        <div class="border border-gray-200 rounded-lg">
          <button
            class="w-full p-6 flex items-center justify-between text-left hover:bg-am-blue-15 transition-colors rounded-lg"
            on:click={() => toggleAccordion(i)}
            aria-expanded={activeIndex === i}
            aria-controls="content-{i}"
          >
            <span class="text-lg font-semibold text-am-black">{faq.question}</span>
            <svg
              class="w-5 h-5 transform transition-transform duration-200 {activeIndex === i ? 'rotate-180' : ''}"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {#if activeIndex === i}
            <div id="content-{i}" transition:slide={{ duration: 200 }} class="px-6 pb-4 mt-4">
              <p class="text-am-black-75 leading-relaxed">
                {faq.answer}
              </p>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  </div>
</section>

<style>
  button {
    -webkit-tap-highlight-color: transparent;
  }

  button:focus-visible {
    outline: 2px solid #536aff;
    outline-offset: -2px;
  }
</style>
